import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { WorkspacePremium as WorkspacePremiumIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { hostApiClient } from '../../hostApiClient';
import { Organization, OrganizationType } from '../../models/Organization';
import {
  OrganizationSubscriptionStatus,
  SubscriptionStatus,
} from '../../models/Subscription';
import SubscriptionPlans from '../../components/SubscriptionPlans';
import CommissionChargesPanel from '../../components/subscription/CommissionChargesPanel';
import CurrentSubscriptionCard from '../../components/subscription/CurrentSubscriptionCard';
import SubscriptionInvoiceHistory from '../../components/subscription/SubscriptionInvoiceHistory';
import SubscriptionManagePanel from '../../components/subscription/SubscriptionManagePanel';
import SubscriptionPaymentMethodPanel from '../../components/subscription/SubscriptionPaymentMethodPanel';
import {
  cacheOrganizationType,
  clearOnboardingDisplayName,
  clearSubscriptionOnboardingActive,
  isSubscriptionOnboardingActive,
  readSubscriptionOnboardingNext,
  resolveOnboardingContinuePath,
} from '../../utils/subscriptionOnboarding';
import {
  clearSubscriptionAwaitingStripe,
  hasSubscriptionWebhookCaughtUp,
  notifyOrganizationTypeUpdated,
  readSubscriptionAwaitingStripe,
  STRIPE_RETURN_POLL_INTERVAL_MS,
  STRIPE_RETURN_POLL_TIMEOUT_MS,
} from '../../utils/subscriptionStripeReturn';

const MySubscriptionPageContent: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();

  const [organization, setOrganization] = useState<Organization | null>(null);
  const [subscription, setSubscription] = useState<OrganizationSubscriptionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const [syncingAfterStripe, setSyncingAfterStripe] = useState(
    () => Boolean(readSubscriptionAwaitingStripe()),
  );
  const [stripeSyncTimedOut, setStripeSyncTimedOut] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [portalError, setPortalError] = useState<string | null>(null);
  const lastOrgTypeRef = useRef<OrganizationType | null | undefined>(undefined);

  const applyBillingState = useCallback((org: Organization, status: OrganizationSubscriptionStatus) => {
    setOrganization(org);
    setSubscription(status);
    const previous = lastOrgTypeRef.current;
    const next = org.organizationType ?? null;
    lastOrgTypeRef.current = next;
    if (previous !== undefined && next && previous !== next) {
      notifyOrganizationTypeUpdated(next);
    }
  }, []);

  const refreshBillingState = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [org, status] = await Promise.all([
        hostApiClient.getOrganization(),
        hostApiClient.getSubscription(),
      ]);
      applyBillingState(org, status);
      return { org, status };
    } catch (err) {
      setError(t('subscription.fetchError', 'Failed to fetch organization subscription'));
      console.error('Error fetching subscription state:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [applyBillingState, t]);

  useEffect(() => {
    void refreshBillingState();
  }, [refreshBillingState]);

  useEffect(() => {
    const baseline = readSubscriptionAwaitingStripe();
    if (!baseline) {
      setSyncingAfterStripe(false);
      return;
    }

    setSyncingAfterStripe(true);
    setStripeSyncTimedOut(false);
    let cancelled = false;
    let intervalId: ReturnType<typeof setInterval> | undefined;

    const finish = (timedOut: boolean) => {
      clearSubscriptionAwaitingStripe();
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = undefined;
      }
      if (!cancelled) {
        setSyncingAfterStripe(false);
        if (timedOut) {
          setStripeSyncTimedOut(true);
        }
      }
    };

    const pollOnce = async () => {
      if (Date.now() - baseline.startedAt > STRIPE_RETURN_POLL_TIMEOUT_MS) {
        finish(true);
        return;
      }
      try {
        const [org, status] = await Promise.all([
          hostApiClient.getOrganization(),
          hostApiClient.getSubscription(),
        ]);
        if (cancelled) return;
        applyBillingState(org, status);
        if (
          hasSubscriptionWebhookCaughtUp(baseline, {
            planCode: status.planCode,
            status: status.status,
            organizationType: org.organizationType,
          })
        ) {
          finish(false);
        }
      } catch (err) {
        console.error('Error polling subscription after Stripe return:', err);
      }
    };

    void pollOnce();
    intervalId = setInterval(() => {
      void pollOnce();
    }, STRIPE_RETURN_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [applyBillingState]);

  const openPortal = async () => {
    try {
      setPortalLoading(true);
      setPortalError(null);
      // Generic manage/cancel portal — do not enter post-Stripe sync polling.
      const { url } = await hostApiClient.createSubscriptionPortal();
      if (url) {
        window.location.assign(url);
      }
    } catch (err) {
      setPortalError(t('subscription.portalError', 'Failed to open billing portal'));
      console.error('Error opening subscription portal:', err);
    } finally {
      setPortalLoading(false);
    }
  };

  const status = subscription?.status ?? null;
  const planCode = subscription?.planCode ?? null;
  const hasActivatedSubscription = Boolean(status && planCode);
  const openSubscriptionStatuses = new Set<SubscriptionStatus>([
    SubscriptionStatus.ACTIVE,
    SubscriptionStatus.TRIALING,
    SubscriptionStatus.PAST_DUE,
    SubscriptionStatus.PAUSED,
  ]);
  /** Reactivation / incomplete — show catalogue on this page. Switches use /host/subscription/change. */
  const showInlinePlanPicker =
    !hasActivatedSubscription ||
    (status != null && !openSubscriptionStatuses.has(status));

  const periodCaption = (() => {
    if (!subscription || !status) return null;
    if (
      status !== SubscriptionStatus.TRIALING &&
      status !== SubscriptionStatus.ACTIVE &&
      status !== SubscriptionStatus.PAST_DUE
    ) {
      return null;
    }
    const rawEnd =
      status === SubscriptionStatus.TRIALING
        ? subscription.trialEndsAt ?? subscription.currentPeriodEndsAt
        : subscription.currentPeriodEndsAt;
    if (!rawEnd) return null;
    const date = new Date(rawEnd).toLocaleDateString();
    if (subscription.cancelAtPeriodEnd === true) {
      return t('subscription.accessUntil', 'Access until {{date}}', { date });
    }
    if (status === SubscriptionStatus.TRIALING) {
      return t('subscription.trialEndsOn', 'Trial ends {{date}}', { date });
    }
    return t('subscription.nextPaymentOn', 'Next payment {{date}}', { date });
  })();

  if (loading && !subscription) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !subscription) {
    return (
      <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 960, mx: 'auto' }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        // DashboardLayout `main` is a column flex scroller; without flexShrink:0 the page
        // box is compressed and paddingBottom no longer clears the fixed site footer.
        flexShrink: 0,
        px: { xs: 2, sm: 3, md: 4 },
        pt: { xs: 2, sm: 3, md: 4 },
        // Same rhythm as Stack spacing between panels; main already clears the fixed footer.
        pb: 2,
        maxWidth: 960,
        width: '100%',
        mx: 'auto',
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        sx={{ mb: 3, pb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Box>
          <Stack direction="row" spacing={1.25} alignItems="flex-start">
            <Box
              sx={{
                width: 36,
                height: 36,
                mt: 0.25,
                borderRadius: 1.25,
                display: 'grid',
                placeItems: 'center',
                bgcolor: alpha(theme.palette.primary.main, 0.16),
                border: 1,
                borderColor: alpha(theme.palette.primary.main, 0.28),
                flexShrink: 0,
              }}
            >
              <WorkspacePremiumIcon sx={{ color: 'primary.light', fontSize: 20 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h5" component="h1" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>
                {t('subscription.pageTitle', 'Plan and Billing')}
              </Typography>
              {periodCaption ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
                  {periodCaption}
                </Typography>
              ) : null}
            </Box>
          </Stack>
        </Box>
      </Stack>

      {syncingAfterStripe && (
        <Alert
          severity="info"
          icon={<CircularProgress size={18} />}
          sx={{ mb: 2.5, borderRadius: 2 }}
          onClose={() => {
            clearSubscriptionAwaitingStripe();
            setSyncingAfterStripe(false);
          }}
        >
          {t(
            'subscription.syncingAfterStripe',
            'Checking whether Stripe confirmed your plan change…',
          )}
        </Alert>
      )}

      {stripeSyncTimedOut && !syncingAfterStripe && (
        <Alert
          severity="warning"
          sx={{ mb: 2.5, borderRadius: 2 }}
          onClose={() => setStripeSyncTimedOut(false)}
        >
          {t(
            'subscription.stripeSyncTimedOut',
            'No plan change showed up yet. If you confirmed in Stripe, wait a moment and refresh — local webhooks may still be catching up. If you cancelled, your current plan is unchanged.',
          )}
        </Alert>
      )}

      {portalError && (
        <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }} onClose={() => setPortalError(null)}>
          {portalError}
        </Alert>
      )}

      {hasActivatedSubscription && subscription ? (
        <Stack spacing={3.5}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2.5}
            alignItems={{ xs: 'stretch', md: 'flex-start' }}
          >
            <Box sx={{ flex: { md: '0 1 420px' }, maxWidth: { md: 420 }, width: '100%' }}>
              <CurrentSubscriptionCard subscription={subscription} />
            </Box>
            <Stack spacing={2.5} sx={{ flex: 1, minWidth: 0, width: '100%' }}>
              <SubscriptionManagePanel
                subscription={subscription}
                portalLoading={portalLoading}
                onOpenPortal={() => void openPortal()}
                onSwitchPlan={
                  status != null && openSubscriptionStatuses.has(status)
                    ? () => navigate('/host/subscription/change')
                    : undefined
                }
              />
              <SubscriptionPaymentMethodPanel
                onManage={() => void openPortal()}
                manageLoading={portalLoading}
              />
            </Stack>
          </Stack>
          <CommissionChargesPanel />
          <SubscriptionInvoiceHistory />
          {showInlinePlanPicker && (
            <SubscriptionPlans
              mode="select"
              organizationType={organization?.organizationType}
              currentPlanCode={planCode}
              currentStatus={status}
              onSubscriptionChange={() => {
                void refreshBillingState();
              }}
            />
          )}
        </Stack>
      ) : (
        <SubscriptionPlans
          organizationType={organization?.organizationType}
          currentPlanCode={planCode}
          currentStatus={status}
          onSubscriptionChange={() => {
            void refreshBillingState();
          }}
        />
      )}
    </Box>
  );
};

/**
 * Stripe Checkout success/cancel URL is always `/host/subscription`.
 * After signup plan selection, finish onboarding here and continue to the host app
 * (default `/host/organization`) instead of bouncing through `/onboarding/plan`.
 */
const PostCheckoutOnboardingGate: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const continuePath = resolveOnboardingContinuePath(readSubscriptionOnboardingNext());
  const finishedRef = useRef(false);

  const finish = useCallback(
    (orgType?: OrganizationType | null) => {
      if (finishedRef.current) return;
      finishedRef.current = true;
      clearSubscriptionAwaitingStripe();
      clearOnboardingDisplayName();
      clearSubscriptionOnboardingActive();
      if (orgType) {
        cacheOrganizationType(orgType);
        notifyOrganizationTypeUpdated(orgType);
      }
      navigate(continuePath, { replace: true });
    },
    [continuePath, navigate],
  );

  useEffect(() => {
    const baseline = readSubscriptionAwaitingStripe();
    if (!baseline) {
      finish();
      return;
    }

    let cancelled = false;
    let intervalId: ReturnType<typeof setInterval> | undefined;

    const pollOnce = async () => {
      if (Date.now() - baseline.startedAt > STRIPE_RETURN_POLL_TIMEOUT_MS) {
        if (!cancelled) finish();
        return;
      }
      try {
        const [org, status] = await Promise.all([
          hostApiClient.getOrganization(),
          hostApiClient.getSubscription(),
        ]);
        if (cancelled) return;
        if (
          hasSubscriptionWebhookCaughtUp(baseline, {
            planCode: status.planCode,
            status: status.status,
            organizationType: org.organizationType,
          })
        ) {
          finish(org.organizationType);
        }
      } catch (err) {
        console.error('Error polling subscription after Stripe signup return:', err);
      }
    };

    void pollOnce();
    intervalId = setInterval(() => {
      void pollOnce();
    }, STRIPE_RETURN_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
    };
  }, [finish]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      gap={2}
      minHeight={400}
      px={2}
    >
      <CircularProgress />
      <Typography variant="body2" color="text.secondary" textAlign="center">
        {t(
          'subscription.syncingAfterStripe',
          'Checking whether Stripe confirmed your plan change…',
        )}
      </Typography>
    </Box>
  );
};

const MySubscriptionPage: React.FC = () => {
  if (isSubscriptionOnboardingActive()) {
    return <PostCheckoutOnboardingGate />;
  }
  return <MySubscriptionPageContent />;
};

export default MySubscriptionPage;
